import { User } from '../auth/User';
import {
  Location,
  LocationV2,
  CreateLocationV2Request,
  YextCategory,
  TimezoneDtoType,
} from '../types/Location';
import { ApiRoutes, ApiParams, getFullApiRoute, replaceRouteParams } from './ApiRoutes';
import { HttpRequest } from './HttpRequest';

export interface LocationVerificationStatus {
  pendingLocationVerification: boolean;
  pendingLocationVerificationCount: number;
  showPopup: boolean;
  promptCount: number;
}

export class LocationAPI {
  //This will get all locations for current user tenantId
  static async getAll(user: User): Promise<Location[]> {
    const result = await HttpRequest(
      replaceRouteParams(getFullApiRoute(ApiRoutes.LOCATIONS), { id: user.attributes.tenant_id }),
      'GET',
      null,
      null,
      user,
    );
    return result?.data || [];
  }

  //Get all locations for a multiple company by IDs
  static async getByCompanyIds(
    user: User,
    companyIds: string[],
    fetchLocationsForInvite = false,
  ): Promise<Array<{ companyId: string; locations: Location[] }>> {
    const result = await HttpRequest(
      getFullApiRoute(fetchLocationsForInvite ? ApiRoutes.COMPANY_LOCATIONS_BY_IDS_V3 : ApiRoutes.COMPANY_LOCATIONS_BY_IDS_FOR_INVITE_V3),
      'POST',
      { companyIds },
      null,
      user,
    );
    return result?.data || [];
  }

  //Get all locations for a specific company by ID
  static async getByCompanyId(user: User, companyId: string): Promise<Location[]> {
    const result = await HttpRequest(
      replaceRouteParams(getFullApiRoute(ApiRoutes.LOCATIONS), { id: companyId }),
      'GET',
      null,
      null,
      user,
    );
    return result?.data || [];
  }

  static async updateOne(user: User, data: Location): Promise<Record<string, unknown> | undefined> {
    const result = await HttpRequest(
      replaceRouteParams(getFullApiRoute(ApiRoutes.LOCATIONS), { id: user.attributes.tenant_id }) +
      `${data.id ? `/${data.id}` : ''}`,
      data.id ? 'PUT' : 'POST',
      data,
      new Headers({ 'Content-Type': 'application/json' }),
      user,
    );

    //Check if we have a result and if it is object or array, if array just retun the first item as that is what we expect
    return result && result.data
      ? Array.isArray(result.data)
        ? result.data[0]
        : result.data
      : undefined;
  }

  // V2 API Methods

  /**
   * Create a new location using V2 API
   */
  static async createV2(
    user: User,
    companyId: string,
    data: CreateLocationV2Request,
  ): Promise<LocationV2 | undefined> {
    const result = await HttpRequest(
      replaceRouteParams(getFullApiRoute(ApiRoutes.LOCATIONS_V3), { companyId }),
      'POST',
      data,
      new Headers({ 'Content-Type': 'application/json' }),
      user,
    );
    return result?.data;
  }

  /**
   * Update a location using V2 API
   */
  static async updateV2(
    user: User,
    companyId: string,
    locationId: string,
    data: CreateLocationV2Request,
  ): Promise<LocationV2 | undefined> {
    const result = await HttpRequest(
      replaceRouteParams(getFullApiRoute(ApiRoutes.LOCATIONS_V3), { companyId }) + `/${locationId}`,
      'PUT',
      data,
      new Headers({ 'Content-Type': 'application/json' }),
      user,
    );
    return result?.data;
  }

  /**
   * Get all locations for a company using V2 API
   */
  static async getAllV2(
    user: User,
    companyId: string,
    page = 1,
    limit = 100,
  ): Promise<LocationV2[]> {
    const url = `${replaceRouteParams(getFullApiRoute(ApiRoutes.LOCATIONS_V3), { companyId })}?page=${page}&limit=${limit}`;
    const result = await HttpRequest(url, 'GET', null, null, user);
    return result?.data || [];
  }

  static async getVerificationStatus(user: User): Promise<LocationVerificationStatus | undefined> {
    const result = await HttpRequest(
      getFullApiRoute(ApiRoutes.LOCATION_VERIFICATION_STATUS_V3),
      'GET',
      null,
      null,
      user,
    );
    return Array.isArray(result?.data) ? result.data[0] : result?.data;
  }

  static async remindLaterForVerification(user: User): Promise<boolean> {
    const result = await HttpRequest(
      getFullApiRoute(ApiRoutes.LOCATION_VERIFICATION_REMIND_LATER_V3),
      'POST',
      null,
      null,
      user,
    );
    const data = result?.data;
    return Array.isArray(data) ? data[0] === true : data === true;
  }

  /**
   * Get a single location by ID using V2 API
   */
  static async getOneV2(
    user: User,
    companyId: string,
    locationId: string,
  ): Promise<LocationV2 | undefined> {
    const result = await HttpRequest(
      replaceRouteParams(getFullApiRoute(ApiRoutes.LOCATIONS_V3), { companyId }) + `/${locationId}`,
      'GET',
      null,
      null,
      user,
    );
    // API returns data as array, extract first item
    const data = result?.data;
    return Array.isArray(data) ? data[0] : data;
  }

  /**
   * Delete a location using V2 API
   */
  static async deleteV2(user: User, companyId: string, locationId: string): Promise<boolean> {
    // HttpRequest throws on error, so if we get here the request was successful
    await HttpRequest(
      replaceRouteParams(getFullApiRoute(ApiRoutes.LOCATIONS_V3), { companyId }) + `/${locationId}`,
      'DELETE',
      null,
      null,
      user,
    );
    return true;
  }

  /**
   * Get filter options for locations (statuses, store types, offerings)
   */
  static async getFilterOptionsV2(
    user: User,
    companyId: string,
  ): Promise<{ statuses: string[]; storeTypes: string[]; offerings: string[] } | undefined> {
    const result = await HttpRequest(
      replaceRouteParams(getFullApiRoute(ApiRoutes.LOCATIONS_V3), { companyId }) +
        '/filters-options',
      'GET',
      null,
      null,
      user,
    );
    // API returns data as array, extract first item
    const data = result?.data;
    return Array.isArray(data) ? data[0] : data;
  }

  /**
   * Filter locations with query parameters
   */
  static async filterV2(
    user: User,
    companyId: string,
    params: {
      keyword?: string;
      status?: string;
      type?: string[];
      offering?: string[];
      page?: number;
      limit?: number;
    },
  ): Promise<{ data: LocationV2[]; totalCount: number }> {
    const searchParams = new URLSearchParams();

    if (params.keyword) searchParams.append('keyword', params.keyword);
    if (params.status) searchParams.append('status', params.status);
    if (params.type?.length) {
      params.type.forEach((t) => searchParams.append('type', t));
    }
    if (params.offering?.length) {
      params.offering.forEach((o) => searchParams.append('offering', o));
    }
    searchParams.append('page', String(params.page || 1));
    searchParams.append('limit', String(params.limit || 100));

    const url = `${replaceRouteParams(getFullApiRoute(ApiRoutes.LOCATIONS_V3), { companyId })}/filter?${searchParams.toString()}`;
    const result = await HttpRequest(url, 'GET', null, null, user);

    return {
      data: result?.data || [],
      totalCount: result?.metadata?.totalCount || result?.data?.length || 0,
    };
  }

  /**
   * Search offerings for a company's locations
   */
  static async searchOfferings(
    user: User,
    companyId: string,
    search: string,
    params: ApiParams,
  ): Promise<any | undefined> {
    const urlParams = new URLSearchParams({
      page: String(params.page),
      limit: String(params.per_page),
    });

    if (search?.trim()) {
      urlParams.append('search', search);
    }

    const result = await HttpRequest(
      `${replaceRouteParams(getFullApiRoute(ApiRoutes.LOCATIONS_V3_OFFERINGS), { companyId })}?${urlParams.toString()}`,
      'GET',
      null,
      null,
      user,
    );
    return result && result.data ? result : undefined;
  }

  /**
   *
   * @param user - Current user
   * @param filters - Filters for fetching locations
   * @returns - Locations associated with the current user
   */
  static async getCurrentUserLocations(
    user: User,
    filters: {
      keyword?: string;
      page: number;
      limit: number;
    },
  ): Promise<any | undefined> {
    const searchParams = new URLSearchParams();

    // Append query parameters based on the provided filters
    if (filters.keyword) searchParams.append('keyword', filters.keyword);
    searchParams.append('page', String(filters.page));
    searchParams.append('limit', String(filters.limit));

    const result = await HttpRequest(
      getFullApiRoute(ApiRoutes.CURRENT_USER_LOCATIONS_V3) + `?${searchParams.toString()}`,
      'GET',
      null,
      new Headers(),
      user,
    );

    return result && result.data ? result : undefined;
  }

  /**
   *
   * @param user - Current user
   * @param filters - Filters for fetching locations
   * @param userId - to fetch data of specific User
   * @returns - Locations associated with the current user
   */
  static async getUserLocations(
    user: User,
    filters: {
      keyword?: string;
      page: number;
      limit: number;
    },
    userId: string,
  ): Promise<any | undefined> {
    const searchParams = new URLSearchParams();

    // Append query parameters based on the provided filters
    if (filters.keyword) searchParams.append('keyword', filters.keyword);
    searchParams.append('page', String(filters.page));
    searchParams.append('limit', String(filters.limit));

    const result = await HttpRequest(
      getFullApiRoute(replaceRouteParams(ApiRoutes.USER_LOCATIONS_V3, { userId })) +
        `?${searchParams.toString()}`,
      'GET',
      null,
      new Headers(),
      user,
    );

    return result && result.data ? result : undefined;
  }

  /**
   *
   * @param user - Current user
   * @param locationId - ID of the location to disable
   * @returns - Result of the disable operation
   */
  static async disableCurrentUserLocation(
    user: User,
    {
      companyId,
      locationId,
      data,
    }: { companyId: string; locationId: string; data: { status: string } },
  ): Promise<any | undefined> {
    const result = await HttpRequest(
      getFullApiRoute(
        replaceRouteParams(ApiRoutes.CURRENT_USER_DISABLE_LOCATION_V3, { companyId, locationId }),
      ),
      'PUT',
      data,
      new Headers({ 'Content-Type': 'application/json' }),
      user,
    );

    return result && result.data ? result : undefined;
  }

  /**
   *
   * @param user - Current user
   * @param companyId - ID of the company of location
   * @param locationId - ID of the location to disable
   * @returns - Result of the disable operation
   */
  static async manageUserLocation(
    user: User,
    userId: string,
    data: { companyId: string; locationId: string; hasAccess: boolean },
  ): Promise<any | undefined> {
    const result = await HttpRequest(
      getFullApiRoute(replaceRouteParams(ApiRoutes.MANAGE_USER_LOCATION_V3, { userId })),
      'POST',
      data,
      new Headers({ 'Content-Type': 'application/json' }),
      user,
    );

    return result && result.data ? result : undefined;
  }

  /**
   *
   * @param user - Current user
   * @param data - Object containing companyId, locationId, and hasAccess boolean
   * @returns - Result of the manage location operation
   */
  static async manageCurrentUserLocation(
    user: User,
    data: { companyId: string; locationId: string; hasAccess: boolean },
  ): Promise<any | undefined> {
    const result = await HttpRequest(
      getFullApiRoute(ApiRoutes.CURRENT_USER_MANAGE_LOCATION_V3),
      'POST',
      data,
      new Headers({ 'Content-Type': 'application/json' }),
      user,
    );

    return result && result.data ? result : undefined;
  }

  /**
   * Get Yext categories for a company
   */
  static async getYextCategories(user: User, companyId: string): Promise<YextCategory[]> {
    const result = await HttpRequest(
      replaceRouteParams(getFullApiRoute(ApiRoutes.LOCATIONS_YEXT_CATEGORIES_V3), { companyId }),
      'GET',
      null,
      null,
      user,
    );
    return result?.data || [];
  }

  /**
   * Get IANA timezone names available for location selection.
   */
  static async getTimezones(user: User, companyId: string): Promise<TimezoneDtoType[]> {
    const result = await HttpRequest(
      replaceRouteParams(getFullApiRoute(ApiRoutes.LOCATIONS_TIMEZONES_V3), { companyId }),
      'GET',
      null,
      null,
      user,
    );
    return result?.data || [];
  }
}
